import App from './App.vue'
import { createApp as createCsrApp, createSSRApp } from 'vue'
import { createPinia } from 'pinia'
import { createPersistedState, type StorageLike } from 'pinia-plugin-persistedstate'
import { useI18n } from '@/composables/I18n'
import taptic from '@/plugins/directives/taptic'
import { createRouter } from '@/composables/useRouter'
import { createHttp } from '@/composables/useHttp'
import type { SSRContext } from '@/types/ssr/SSRContext'
import { PlatformPlugin } from '@/plugins/platformPlugin'
import { createPreferencesStorage } from '@/stores/storage/PreferencesStorage'
import { EventBus, eventPluginKey } from '@/events/EventBus'
import { TrackingPlugin } from '@/plugins/TrackingPlugin'

export async function createApp(ssrContext?: SSRContext) {
  const app = import.meta.env.SSR ? createSSRApp(App) : createCsrApp(App)

  const i18n = useI18n()
  app.use(i18n)

  app.directive('taptic', taptic)

  const pinia = createPinia()

  if (!import.meta.env.SSR) {
    const storageDriver = await createPiniaStorageDriver()

    pinia.use(createPersistedState({
      storage: storageDriver,
    }))
  }

  app.use(pinia)

  const router = createRouter(app, ssrContext)

  app.use(router)

  const eventBus = new EventBus()

  app.provide(eventPluginKey, eventBus)

  const http = createHttp(router)

  app.use(PlatformPlugin)
  app.use(TrackingPlugin, { http })

  return { app, router, pinia, eventBus: eventBus }
}

const createPiniaStorageDriver = async (): Promise<StorageLike> => {
  if (__CAPACITOR__) {
    const storage = createPreferencesStorage()
    await storage.init()

    return storage
  }

  return localStorage
}